import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

import { DataTable } from '@/components/ui/data-table';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { cn } from '@/lib/utils';

import { myCasesQueries, type TaskInboxStatus } from '../queries';
import { usePersonNames } from '../hooks/usePersonNames';
import { findTaskSortOption } from '../lib/taskInboxSort';
import { groupTasksByEntity, sortGroups, type TaskGroup } from '../lib/taskInboxGroups';
import { TaskInboxRowItem, type OnEditTask } from './TaskInboxRowItem';
import { TaskInboxSkeleton } from './TaskInboxSkeleton';

/**
 * How many tasks the grouped view pulls in one page before clustering
 * client-side. A personal worklist is small; the caption below flags when the
 * caller has more than this so the grouping isn't silently partial.
 */
const GROUPED_FETCH_SIZE = 200;

interface TaskInboxGroupedProps {
  readonly assignee: string;
  readonly status: TaskInboxStatus;
  readonly sortId: string;
  readonly onEdit: OnEditTask;
}

/**
 * "By patient" view (PTV2-1321 bonus): loads a page of the caller's tasks and
 * clusters them under each owning record. Groups are ordered by record name;
 * tasks within a group follow the selected sort.
 */
export function TaskInboxGrouped({
  assignee,
  status,
  sortId,
  onEdit,
}: TaskInboxGroupedProps): ReactElement {
  const { t } = useTranslation();
  const query = useQuery(
    myCasesQueries.tasks({ assignee, status, page: 0, size: GROUPED_FETCH_SIZE }),
  );
  const items = useMemo(() => query.data?.items ?? [], [query.data]);

  const personGuids = useMemo<readonly string[]>(
    () =>
      items.flatMap((task) =>
        task.entityType === 'person' && task.entityGuid !== null ? [task.entityGuid] : [],
      ),
    [items],
  );
  const personNames = usePersonNames(personGuids);
  const groups = useMemo(
    () => sortGroups(groupTasksByEntity(items, personNames), findTaskSortOption(sortId)),
    [items, personNames, sortId],
  );

  const totalElements = query.data?.totalElements ?? items.length;
  const truncated = totalElements > items.length;

  if (query.isPending) return <TaskInboxSkeleton />;

  if (query.isError) {
    return (
      <div className="max-w-xl">
        <FetchErrorCallout
          error={query.error}
          heading={t('routes.myCases.inbox.error.heading')}
          message={t('routes.myCases.inbox.error.message')}
          codeLabel={t('routes.myCases.inbox.error.codeLabel')}
          onRetry={() => {
            query.refetch().catch(() => undefined);
          }}
          retryLabel={t('routes.myCases.inbox.error.retry')}
        />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" role="status">
        {t('routes.myCases.inbox.empty')}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {truncated && (
        <p className="text-xs text-muted-foreground" role="status">
          {t('routes.myCases.inbox.grouped.truncated', {
            shown: items.length,
            total: totalElements,
          })}
        </p>
      )}
      <DataTable aria-label={t('routes.myCases.inbox.listAriaLabel')}>
        {groups.map((group, index) => (
          <section key={group.key} aria-label={groupLabel(group, t)}>
            <GroupHeader group={group} withTopBorder={index > 0} />
            <ul className="divide-y divide-border">
              {group.tasks.map((task) => (
                <TaskInboxRowItem
                  key={task.id}
                  task={task}
                  entityName={group.name}
                  onEdit={onEdit}
                  showRecordLink={false}
                />
              ))}
            </ul>
          </section>
        ))}
      </DataTable>
    </div>
  );
}

function GroupHeader({
  group,
  withTopBorder,
}: {
  readonly group: TaskGroup;
  readonly withTopBorder: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const label = groupLabel(group, t);
  const count = (
    <span className="text-[11px] text-muted-foreground tabular-nums">
      {t('routes.myCases.inbox.grouped.taskCount', { count: group.tasks.length })}
    </span>
  );
  const base = cn(
    'flex items-center gap-2 border-b border-border bg-muted/40 px-4 py-1.5',
    withTopBorder && 'border-t',
  );
  const recordPath =
    group.entityGuid === null
      ? null
      : group.entityType === 'family'
        ? `/families/${group.entityGuid}`
        : `/patients/${group.entityGuid}`;

  if (recordPath === null) {
    return (
      <div className={base}>
        <span className="text-xs font-semibold text-foreground">{label}</span>
        {count}
      </div>
    );
  }

  return (
    <Link
      to={recordPath}
      className={cn(
        base,
        'group/hdr transition-colors hover:bg-muted/70 focus-visible:bg-muted/70 focus-visible:outline-hidden',
      )}
    >
      <span className="text-xs font-semibold text-primary group-hover/hdr:underline">{label}</span>
      {count}
      <span className="ml-auto inline-flex items-center gap-0.5 text-[11px] font-medium text-muted-foreground opacity-0 transition-opacity group-hover/hdr:opacity-100 group-focus-visible/hdr:opacity-100">
        {t('routes.myCases.inbox.grouped.viewRecord')}
      </span>
      <ChevronRight
        className="h-3.5 w-3.5 shrink-0 text-muted-foreground group-hover/hdr:text-primary"
        aria-hidden="true"
      />
    </Link>
  );
}

function groupLabel(group: TaskGroup, t: (key: string) => string): string {
  if (group.name !== null) return group.name;
  if (group.entityGuid === null) return t('routes.myCases.inbox.grouped.noRecord');
  return group.entityType === 'family'
    ? t('routes.myCases.inbox.unknownFamily')
    : t('routes.myCases.inbox.unknownPatient');
}
